import { useTranslation } from 'react-i18next';
import type { SecondBucket } from '../perSecond';
import { axisMax, LegendItem, TimeChart, type TimeAxis } from './TimeChart';
import { latest } from './values';

/** Stacked from the bottom; `late` only occurs over TCP, where nothing is lost. */
const SERIES = [
    { key: 'lostUp', fill: 'fill-warning', swatch: 'bg-warning', label: 'TestSuite.Line.Legend.Up' },
    { key: 'lostDown', fill: 'fill-primary', swatch: 'bg-primary', label: 'TestSuite.Line.Legend.Down' },
    { key: 'lostUnknown', fill: 'fill-gray-400', swatch: 'bg-gray-400', label: 'TestSuite.Line.Legend.Unknown' },
    { key: 'late', fill: 'fill-accent', swatch: 'bg-accent', label: 'TestSuite.Line.Legend.Late' }
] as const;

const total = (b: SecondBucket) => b.lostUp + b.lostDown + b.lostUnknown + b.late;

/** Lost probes per second as stacked bars, empty where none were lost. */
export const LossChart = ({ slots, axis }: { slots: SecondBucket[]; axis: TimeAxis }) => {
    const { t } = useTranslation();
    const last = latest(slots, (b) => b.sent > 0);
    const yMax = axisMax(slots.map(total), 1);

    return (
        <TimeChart
            title={t('TestSuite.Line.Chart.Loss.Title')}
            ariaLabel={t('TestSuite.Line.Chart.Loss.Aria', {
                up: last?.lostUp ?? 0,
                down: last?.lostDown ?? 0,
                unknown: last?.lostUnknown ?? 0,
                late: last?.late ?? 0
            })}
            legend={SERIES.map((s) => (
                <LegendItem key={s.key} swatch={s.swatch} label={t(s.label)} />
            ))}
            axis={axis}
            yMax={yMax}
            unit=""
        >
            {({ x, y }) => {
                // One path per series, a rectangle per slot: hundreds of <rect> elements cost a render every second.
                const paths: Record<(typeof SERIES)[number]['key'], string[]> = { lostUp: [], lostDown: [], lostUnknown: [], late: [] };
                for (const b of slots) {
                    if (total(b) === 0) continue;
                    const left = x(b.second);
                    const width = Math.max(1, x(b.second + axis.slot) - left - 0.5);
                    let below = 0;
                    for (const s of SERIES) {
                        const n = b[s.key];
                        if (n === 0) continue;
                        const bottom = y(below);
                        below += n;
                        const top = y(below);
                        paths[s.key].push(`M${left},${top}h${width}V${bottom}h${-width}z`);
                    }
                }
                return SERIES.map((s) => <path key={s.key} d={paths[s.key].join('')} className={s.fill} />);
            }}
        </TimeChart>
    );
};
